import { formatDurationRangeLabel } from "@clipboard-health/date-time";
import { TextSkeleton } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { Alert, Stack } from "@mui/material";
import { CancellationCreditProgressBar } from "@src/appV2/redesign/EmergencyCredits/components/CancellationCreditProgressBar";
import { useCallback, useEffect, useState } from "react";

import { ScoreBreakdownList } from "./components/ScoreBreakdownList";
import { ScoreDisplay } from "./components/ScoreDisplay";
import { ShiftCancelFooter } from "./components/ShiftCancelFooter";
import { ShiftCancelHeader } from "./components/ShiftCancelHeader";
import type { ShiftCancelPageData } from "./hooks/useShiftCancelPageData";

export interface ShiftCancelPageContentHandlers {
  isCancelling: boolean;
  onKeepShift: () => void;
  onPressAndHoldToCancel: () => void;
  onWhyCantIUseCredit?: () => void;
}

export type ShiftCancelPageContentProps = ShiftCancelPageData & ShiftCancelPageContentHandlers;

function ShiftCancelPageContentSkeleton(): JSX.Element {
  return (
    <>
      <ShiftCancelHeader />
      <Stack spacing={4} alignItems="center" sx={{ paddingY: 8, paddingX: 6 }}>
        <TextSkeleton variant="h2" width="60%" />
        <TextSkeleton variant="body2" width="80%" />
        <TextSkeleton variant="h1" width="30%" />
        <TextSkeleton variant="body2" width="100%" />
        <TextSkeleton variant="body2" width="100%" />
      </Stack>
    </>
  );
}

export function ShiftCancelPageContent(props: ShiftCancelPageContentProps): JSX.Element | null {
  const [isCreditPreviewed, setIsCreditPreviewed] = useState(false);
  const [isPreviewAnimating, setIsPreviewAnimating] = useState(false);

  const handlePreviewCredit = useCallback(() => {
    setIsCreditPreviewed(true);
    setIsPreviewAnimating(true);
  }, []);

  const handlePreviewAnimationComplete = useCallback(() => {
    setIsPreviewAnimating(false);
  }, []);

  // Automatically apply the credit for workers who are about to become restricted anyway.
  const isDataReady = props.enabled && !props.isLoading && !props.isError;
  const shouldAutoApplyCredit = isDataReady && props.isBecomingRestricted && props.canUseCredit;
  useEffect(() => {
    if (shouldAutoApplyCredit) {
      setIsCreditPreviewed(true);
    }
  }, [shouldAutoApplyCredit]);

  if (!props.enabled || props.isLoading) {
    return <ShiftCancelPageContentSkeleton />;
  }

  if (props.isError) {
    return (
      <>
        <ShiftCancelHeader />
        <Alert severity="error" sx={{ margin: 4 }}>
          Something went wrong while loading cancellation details. Please try again.
        </Alert>
      </>
    );
  }

  const {
    isWorkerRestricted,
    cancellationPolicy,
    currentScore,
    baseScoreChange,
    newScoreBeforeCredit,
    creditBenefit,
    finalScore,
    initialECStats,
    finalECStats,
    hasCancellationCredit,
    isWithinECWindow,
    canUseCredit,
    isCancelling,
    onKeepShift,
    onPressAndHoldToCancel,
    onWhyCantIUseCredit,
  } = props;

  const isZeroPointsCancellation = !isWorkerRestricted && baseScoreChange === 0;
  const isPendingPreview =
    !isWorkerRestricted && !isZeroPointsCancellation && canUseCredit && !isCreditPreviewed;

  const showScoreSection =
    !isWorkerRestricted &&
    !isZeroPointsCancellation &&
    isDefined(initialECStats) &&
    isDefined(finalECStats);

  const [from, to] = cancellationPolicy?.leadTime ?? [0, undefined];
  const policyLabel = `${formatDurationRangeLabel(from ?? 0, to ?? undefined, "hour", {
    format: "abbreviated",
    keepOriginalUnit: true,
  })} cancellation`;

  const showWhyCantIUseCredit =
    !isWorkerRestricted &&
    !isZeroPointsCancellation &&
    !canUseCredit &&
    hasCancellationCredit &&
    typeof onWhyCantIUseCredit === "function";

  return (
    <>
      <ShiftCancelHeader
        isWorkerRestricted={isWorkerRestricted}
        isZeroPointsCancellation={isZeroPointsCancellation}
        verticallyCenter={!showScoreSection}
      />

      {showScoreSection && (
        <Stack sx={{ flex: 1, paddingY: 8 }}>
          <ScoreDisplay
            score={isPendingPreview ? newScoreBeforeCredit : finalScore}
            variant={isCreditPreviewed ? "credit-applied" : "penalized"}
          />

          {/* Only render progress bar for <24h cancellations */}
          {isWithinECWindow && (
            <Stack sx={{ paddingX: 6, paddingY: 4 }}>
              <CancellationCreditProgressBar
                showShiftsProgress={!hasCancellationCredit}
                cancellationCreditStats={isCreditPreviewed ? finalECStats : initialECStats}
                mode={isCreditPreviewed ? "drain" : "static"}
                label={isCreditPreviewed ? "Applying credit..." : undefined}
                onAnimationComplete={handlePreviewAnimationComplete}
              />
            </Stack>
          )}

          <ScoreBreakdownList
            currentScore={currentScore}
            baseScoreChange={baseScoreChange}
            policyLabel={policyLabel}
            creditBenefit={isCreditPreviewed ? creditBenefit : undefined}
          />
        </Stack>
      )}

      <ShiftCancelFooter
        variant={isPendingPreview ? "preview-credit" : "cancel-shift"}
        showWhyCantIUseCredit={showWhyCantIUseCredit}
        isCancelling={isCancelling}
        isPreviewAnimating={isPreviewAnimating}
        onKeepShift={onKeepShift}
        onPressAndHoldToCancel={onPressAndHoldToCancel}
        onPreviewCredit={handlePreviewCredit}
        onWhyCantIUseCredit={onWhyCantIUseCredit}
      />
    </>
  );
}
